import React, { useMemo, useState } from 'react';
import { Example } from '../types';
import { HUMAN_MODEL } from '../utils/entityUtils';
import { buildConfusionMatrix, ConfusionCell } from '../utils/confusion';
import { parseLabelColor } from '../utils/labelColor';
import ConfirmModal from './ConfirmModal';

/**
 * BQ-20: Cross-model label confusion matrix UI.
 *
 * Hosts two annotator dropdowns (any model name OR HUMAN_MODEL='Gold'),
 * renders the matrix produced by `buildConfusionMatrix` as a Tailwind heat-map
 * table, and surfaces a "Suggest merge" affordance on off-diagonal cells whose
 * `suggestionScore >= 0.8`. Clicking the suggest icon opens a shared
 * <ConfirmModal>; on confirm we call `onMergeLabels(from, to)` which the
 * parent wires to `handleMergeLabel`.
 *
 * Off-diagonal cells are also click-to-inspect: clicking the cell body opens
 * a small inline list of example IDs where the (rowLabel, colLabel) confusion
 * appears, so the user can navigate to those rows. We keep this rendering
 * deliberately simple (a list of IDs with the example text snippet) — the
 * full DiffView is owned by ComparisonView itself.
 */

const SUGGEST_THRESHOLD = 0.8;

export interface LabelConfusionMatrixProps {
  examples: Example[];
  modelNames: string[];
  customLabelColors?: Record<string, string>;
  /** Wired by parent to App-level handleMergeLabel. */
  onMergeLabels?: (from: string, to: string) => void;
}

interface MergePending {
  from: string;
  to: string;
}

interface ExamineCell {
  row: string;
  col: string;
}

const LabelConfusionMatrix: React.FC<LabelConfusionMatrixProps> = ({
  examples,
  modelNames,
  customLabelColors,
  onMergeLabels,
}) => {
  // Annotator options: every real model + the synthetic Gold side.
  const sideOptions = useMemo(() => {
    const sorted = [...modelNames].sort((a, b) => a.localeCompare(b));
    return [...sorted, HUMAN_MODEL];
  }, [modelNames]);

  // Default: first model alphabetical vs Gold.
  const defaultSideA = sideOptions.find((s) => s !== HUMAN_MODEL) ?? HUMAN_MODEL;
  const [sideA, setSideA] = useState<string>(defaultSideA);
  const [sideB, setSideB] = useState<string>(HUMAN_MODEL);

  const matrix = useMemo(
    () => buildConfusionMatrix(examples, sideA, sideB),
    [examples, sideA, sideB]
  );

  // Find the max cell count for heatmap normalization.
  const maxCount = useMemo(() => {
    let m = 0;
    for (const row of Object.keys(matrix.cells)) {
      for (const col of Object.keys(matrix.cells[row])) {
        const c = matrix.cells[row][col].count;
        if (c > m) m = c;
      }
    }
    return m;
  }, [matrix]);

  const [pendingMerge, setPendingMerge] = useState<MergePending | null>(null);
  const [examineCell, setExamineCell] = useState<ExamineCell | null>(null);

  const cellAt = (row: string, col: string): ConfusionCell | undefined =>
    matrix.cells[row]?.[col];

  const isEmpty = matrix.rows.length === 0 || matrix.cols.length === 0;

  const heatStyle = (count: number): React.CSSProperties => {
    if (count === 0 || maxCount === 0) return {};
    // Light blue (rgba) scaled to count / maxCount, capped at 0.6 so text stays readable.
    const opacity = Math.min(0.6, 0.1 + (count / maxCount) * 0.5);
    return { backgroundColor: `rgba(59, 130, 246, ${opacity})` };
  };

  const labelBorderStyle = (label: string): React.CSSProperties => {
    const color = parseLabelColor(customLabelColors?.[label]);
    return { boxShadow: `inset 0 0 0 2px ${color.border}` };
  };

  // For "examine" mode: collect example IDs (and a short text snippet) where
  // sideA's `row`-labeled entity overlaps sideB's `col`-labeled entity.
  const examineMatches = useMemo(() => {
    if (!examineCell) return [];
    const out: Array<{ exampleId: string; snippet: string }> = [];
    const { row, col } = examineCell;
    for (const ex of examples) {
      const aEnts =
        sideA === HUMAN_MODEL
          ? ex.humanAnnotations ?? []
          : ex.modelResponses.find((r) => r.modelName === sideA)?.entities ?? [];
      const bEnts =
        sideB === HUMAN_MODEL
          ? ex.humanAnnotations ?? []
          : ex.modelResponses.find((r) => r.modelName === sideB)?.entities ?? [];
      for (const a of aEnts) {
        if (a.label !== row) continue;
        if (typeof a.start !== 'number' || typeof a.end !== 'number') continue;
        for (const b of bEnts) {
          if (b.label !== col) continue;
          if (typeof b.start !== 'number' || typeof b.end !== 'number') continue;
          if (a.start < b.end && b.start < a.end) {
            const snippetStart = Math.max(0, Math.min(a.start, b.start) - 20);
            const snippetEnd = Math.min(
              ex.text.length,
              Math.max(a.end, b.end) + 20
            );
            const snippet = ex.text.slice(snippetStart, snippetEnd);
            out.push({ exampleId: ex.id, snippet });
            break;
          }
        }
      }
    }
    return out;
  }, [examineCell, examples, sideA, sideB]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      {/* Side dropdowns */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-xs font-semibold text-gray-600">Side A (rows):</span>
        <select
          value={sideA}
          onChange={(e) => setSideA(e.target.value)}
          className="text-sm px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-label="Confusion matrix side A"
        >
          {sideOptions.map((s) => (
            <option key={`A-${s}`} value={s}>
              {s}
            </option>
          ))}
        </select>
        <span className="text-xs font-semibold text-gray-600">Side B (cols):</span>
        <select
          value={sideB}
          onChange={(e) => setSideB(e.target.value)}
          className="text-sm px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-label="Confusion matrix side B"
        >
          {sideOptions.map((s) => (
            <option key={`B-${s}`} value={s}>
              {s}
            </option>
          ))}
        </select>
        {sideA === sideB && (
          <span className="text-xs text-amber-700">
            Same annotator on both sides — every overlap is on the diagonal by definition.
          </span>
        )}
      </div>

      {/* Matrix */}
      {isEmpty ? (
        <div className="text-sm text-gray-500 italic py-6 text-center">
          No overlapping entities between {sideA} and {sideB} in the current examples.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="text-xs border-collapse">
            <thead>
              <tr>
                <th className="p-2 text-right text-gray-500 font-medium">
                  {sideA} \ {sideB}
                </th>
                {matrix.cols.map((col) => (
                  <th
                    key={`col-${col}`}
                    className="p-2 text-left font-medium text-gray-700"
                    style={labelBorderStyle(col)}
                  >
                    {col}
                    <div className="text-[10px] text-gray-500 font-normal">
                      Σ {matrix.colTotals[col] ?? 0}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.rows.map((row) => (
                <tr key={`row-${row}`}>
                  <th
                    className="p-2 text-right font-medium text-gray-700"
                    style={labelBorderStyle(row)}
                  >
                    {row}
                    <div className="text-[10px] text-gray-500 font-normal">
                      Σ {matrix.rowTotals[row] ?? 0}
                    </div>
                  </th>
                  {matrix.cols.map((col) => {
                    const cell = cellAt(row, col);
                    const count = cell?.count ?? 0;
                    const score = cell?.suggestionScore ?? 0;
                    const isDiagonal = row === col;
                    const showSuggest =
                      !isDiagonal && score >= SUGGEST_THRESHOLD && count > 0;
                    const clickable = !isDiagonal && count > 0;
                    return (
                      <td
                        key={`cell-${row}-${col}`}
                        className={`p-2 border border-gray-200 text-center align-middle ${
                          clickable ? 'cursor-pointer hover:ring-2 hover:ring-blue-400' : ''
                        }`}
                        style={{
                          ...heatStyle(count),
                          ...(isDiagonal ? labelBorderStyle(row) : {}),
                        }}
                        onClick={() => {
                          if (clickable) setExamineCell({ row, col });
                        }}
                        title={
                          count === 0
                            ? '0'
                            : `${count} pairs · score ${(score * 100).toFixed(0)}%`
                        }
                      >
                        <div className="flex items-center justify-center gap-1">
                          <span className="font-medium text-gray-900">{count}</span>
                          {showSuggest && onMergeLabels && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setPendingMerge({ from: row, to: col });
                              }}
                              className="text-amber-700 hover:text-amber-900"
                              title={`Suggest merge: ${row} → ${col} (score ${(score * 100).toFixed(0)}%)`}
                              aria-label={`Suggest merge ${row} into ${col}`}
                            >
                              {/* Lightning-bolt icon = "auto-suggest" */}
                              <svg
                                className="w-3 h-3 inline-block"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                aria-hidden="true"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[11px] text-gray-500 mt-2">
            Cells count overlapping (start, end) pairs between the two annotators
            regardless of label. Diagonal = label agreement; off-diagonal = label
            confusion. The lightning-bolt icon flags merge candidates whose
            asymmetric confidence ≥ {Math.round(SUGGEST_THRESHOLD * 100)}%.
          </p>
        </div>
      )}

      {/* Examine modal: list of examples where this confusion occurs. */}
      {examineCell && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50"
          style={{ zIndex: 10000 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="confusion-examine-title"
          onClick={() => setExamineCell(null)}
        >
          <div
            className="bg-white rounded-lg shadow-2xl border border-gray-200 max-w-xl w-full mx-4 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3
                id="confusion-examine-title"
                className="text-base font-semibold text-gray-900"
              >
                {examineCell.row} ({sideA}) → {examineCell.col} ({sideB})
              </h3>
              <button
                type="button"
                onClick={() => setExamineCell(null)}
                className="text-gray-500 hover:text-gray-800"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="px-5 py-4 text-sm text-gray-700 space-y-2">
              {examineMatches.length === 0 ? (
                <p className="italic text-gray-500">No matches.</p>
              ) : (
                <>
                  <p className="text-xs text-gray-500">
                    {examineMatches.length} example
                    {examineMatches.length === 1 ? '' : 's'} with this confusion:
                  </p>
                  <ul className="space-y-1">
                    {examineMatches.map((m, i) => (
                      <li
                        key={`${m.exampleId}-${i}`}
                        className="border border-gray-200 rounded px-2 py-1 bg-gray-50"
                      >
                        <span className="text-xs font-mono text-gray-500">
                          {m.exampleId}
                        </span>
                        <span className="block text-gray-800">…{m.snippet}…</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Merge confirmation modal — reuses shared <ConfirmModal>. */}
      <ConfirmModal
        isOpen={pendingMerge !== null}
        title="Merge labels"
        destructive
        confirmLabel="Merge"
        message={
          pendingMerge ? (
            <span>
              Merge label <strong>{pendingMerge.from}</strong> ({sideA}) into{' '}
              <strong>{pendingMerge.to}</strong> ({sideB})? This will rewrite
              all entities globally.
            </span>
          ) : (
            ''
          )
        }
        onConfirm={() => {
          if (pendingMerge && onMergeLabels) {
            onMergeLabels(pendingMerge.from, pendingMerge.to);
          }
          setPendingMerge(null);
        }}
        onCancel={() => setPendingMerge(null)}
      />
    </div>
  );
};

export default LabelConfusionMatrix;
